import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const SitesContext = createContext();

// Keep raw site summary values in the simple shape the UI expects for colors, labels, and counts.
const normalizeStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return ['red', 'orange', 'green'].includes(normalized) ? normalized : null;
};

const normalizeFraudLevel = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'low' || normalized === 'medium' || normalized === 'high') {
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
  return null;
};

const normalizeSiteSummary = (site) => ({
  ...site,
  humanClicks: Number(site?.humanClicks || 0),
  botClicks: Number(site?.botClicks || 0),
  fraudLevel: normalizeFraudLevel(site?.fraudLevel),
  status: normalizeStatus(site?.status),
});

export const SitesProvider = ({ children }) => {
  const [sites, setSites] = useState([]);

  const getOrder = useCallback(() => {
    const order = localStorage.getItem('sitesOrder');
    if (order) {
      return order.split(',').map((id) => Number.parseInt(id, 10));
    }
    return [];
  }, []);

  const applyOrder = useCallback((nextSites, order) => {
    const ordered = [];
    let index = 0;

    order.forEach((id) => {
      const site = nextSites.find((candidate) => candidate.id === id);
      if (!site) return;
      ordered.push({ ...site, index });
      index += 1;
    });

    setSites(ordered);
  }, []);

  const fetchSites = useCallback((period = 'Last 30 days') => {
    return fetch(process.env.REACT_APP_SERVER_URL + '/api/get-sites', {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ period })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          const nextSites = data.sites.map(normalizeSiteSummary);
          let order = getOrder();

          for (const site of nextSites) {
            if (!order.includes(site.id)) {
              order.push(site.id);
            }
          }
          order = order.filter((id) => nextSites.find((site) => site.id === id));
          applyOrder(nextSites, order);
          localStorage.setItem('sitesOrder', order);

          return nextSites;
        } else {
          localStorage.removeItem('token');
          window.location.href = '/';
        }
      });
  }, [applyOrder, getOrder]);

  const reorderSites = useCallback((source, destination) => {
    if (!destination || source.index === destination.index) return;

    const order = getOrder();
    const [moved] = order.splice(source.index, 1);
    order.splice(destination.index, 0, moved);
    localStorage.setItem('sitesOrder', order);
    applyOrder(sites, order);
  }, [applyOrder, getOrder, sites]);

  const addSite = useCallback((domain) => {
    return fetch(process.env.REACT_APP_SERVER_URL + '/api/add-site', {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ domain })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          const id = data.website_id;
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          today.setUTCHours(0, 0, 0, 0);
          const lastDay = [today.getUTCDate(), today.getUTCMonth(), today.getUTCFullYear()];

          const newSites = [{
            website: domain,
            id,
            campaigns: [],
            platforms: [],
            detectionLevel: 'true_traffic',
            fraudLevel: 'low',
            data: [{}],
            lifetimeCounts: {
              human: [0],
              bots: [0],
              lastDay,
            },
            lastDay,
            status: 'red',
            humanClicks: 0,
            botClicks: 0,
            previousHumanClicks: null,
            previousBotClicks: null,
          }, ...sites].map(normalizeSiteSummary);

          const order = getOrder();
          order.unshift(id);
          localStorage.setItem('sitesOrder', order);
          applyOrder(newSites, order);

          return { success: true };
        } else if (data.message === 'invalid token!') {
          console.log('unauthorized access');
          localStorage.removeItem('token');
          window.location.href = '/';
          return { success: false, message: data.message };
        } else {
          return { success: false, message: data.message };
        }
      })
      .catch(error => {
        console.log(error);
        return { success: false, message: 'Something went wrong. Please try again.' };
      });
  }, [applyOrder, getOrder, sites]);

  const deleteSite = useCallback((which) => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/delete-site', {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ domain: which.website }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          const id = which.id;
          let order = getOrder();
          order = order.filter((siteId) => siteId !== id);
          localStorage.setItem('sitesOrder', order);
          setSites(sites.filter((site) => site.id !== id));
        } else if (data.message === 'invalid token!') {
          console.log('unauthorized access');
          localStorage.removeItem('token');
          window.location.href = '/';
        }
      })
      .catch(error => console.log(error));
  }, [getOrder, sites]);

  const renameSite = useCallback((which, newDomain) => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/rename-site', {
      method: 'POST',
      headers: {
        'Authorization': localStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ domain: which.website, newDomain }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          const id = which.id;
          setSites(sites.map((site) => {
            if (site.id === id) {
              return {...site, website: newDomain};
            }
            return site;
          }));
        } else if (data.message === 'invalid token!') {
          console.log('unauthorized access');
          localStorage.removeItem('token');
          window.location.href = '/';
        }
      })
      .catch(error => console.log(error));
  }, [sites]);

  const getCampaigns = useCallback((domain) => {
    const site = sites.find((candidate) => candidate.website === domain);
    return ['All', ...(site?.campaigns || [])];
  }, [sites]);

  const getPlatforms = useCallback((domain) => {
    const site = sites.find((candidate) => candidate.website === domain);
    return ['All', ...(site?.platforms || [])];
  }, [sites]);

  const getTotalSince = useCallback((start) => {
    if (sites.length === 0) return 0;

    const [day, month, year] = sites[0].lastDay;
    const startUnix = start.getTime() / 1000;
    let total = 0;

    for (const site of sites) {
      const data = site.data;
      const date = new Date(year, month, day);

      for (let i = data.length - 1; i >= 0; i--) {
        const dateUnix = new Date(date).getTime() / 1000;
        if (dateUnix >= startUnix) {
          for (const value of Object.values(data[i])) {
            total += value[0] + value[1];
          }
        }
        date.setDate(date.getDate() - 1);
      }
    }

    return total;
  }, [sites]);

  const setDetectionLevel = useCallback((id, level) => {
    setSites(sites.map((s) => {
      if (s.id === id) {
        return { ...s, detectionLevel: level };
      }
      return s;
    }));
  }, [sites]);

  const getSites = useCallback(() => sites, [sites]);
  const getSiteByWebsite = useCallback(
    (website) => sites.find((site) => site.website === website) || null,
    [sites]
  );

  const value = useMemo(() => ({
    sites,
    fetchSites,
    getSites,
    getSiteByWebsite,
    getCampaigns,
    getPlatforms,
    reorderSites,
    addSite,
    deleteSite,
    renameSite,
    getTotalSince,
    setDetectionLevel,
  }), [
    sites,
    fetchSites,
    getSites,
    getSiteByWebsite,
    getCampaigns,
    getPlatforms,
    reorderSites,
    addSite,
    deleteSite,
    renameSite,
    getTotalSince,
    setDetectionLevel,
  ]);

  return (
    <SitesContext.Provider value={value}>
      {children}
    </SitesContext.Provider>
  );
};

export const useSites = () => useContext(SitesContext);
